import {
  CustomCalendarModel,
  SimpleActivity,
} from "@ac-expo/api-types";
import { getStartAndEndOfWeek } from "@ac-expo/utils";
import FullCalendar from "@fullcalendar/react";
import {
  EventSchedulesTimes,
  getMinMaxEventTimes,
  getSlots,
  groupActivitiesByWeek,
  groupEventsByTime,
  mergeConsecutiveDaySlots,
} from "@ui/components/ui/planning/utils";
import dayjs, { Dayjs, OpUnitType } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import minMax from "dayjs/plugin/minMax";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import { groupBy, uniq } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useStructureDisplay } from "@ui/components/ui/structure/StructureDisplayContext";

interface PlanningComponentProps {
  activities: SimpleActivity[];
  eventSchedules?: EventSchedulesTimes[];
  hideEmptyDays?: boolean;
  customCalendarModel?: CustomCalendarModel;
}

dayjs.extend(customParseFormat);
dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localizedFormat);
dayjs.extend(minMax);
dayjs.locale("fr");
dayjs.extend(utc);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const usePlanning = ({
  activities,
  eventSchedules,
  hideEmptyDays,
  customCalendarModel,
}: PlanningComponentProps) => {
  const { isPublicExponentDisplay } = useStructureDisplay();

  const getFirstActivityStart = (acts: SimpleActivity[]): Dayjs | null => {
    const starts = acts
      .flatMap((a) => a.schedules.map((s) => dayjs(s.timetable.day).local()))
      .filter((d) => d.isValid());
    if (starts.length === 0) return null;
    return (dayjs.min(starts as unknown as Dayjs[]) as Dayjs) ?? null;
  };

  const computeClosestPrevDistance = (
    acts: SimpleActivity[],
    currentStart: Dayjs
  ) =>
    acts
      .filter((activity) =>
        dayjs(activity.schedules[0].timetable.day).isBefore(currentStart)
      )
      .reduce((minDistance, activity) => {
        const activityDay = dayjs(activity.schedules[0].timetable.day);
        const distance = Math.abs(activityDay.diff(currentStart, "day")) + 1;
        return Math.min(minDistance, distance);
      }, Infinity);

  const computeClosestNextDistance = (
    acts: SimpleActivity[],
    currentStart: Dayjs,
    nextStart: Dayjs
  ) =>
    acts
      .filter((activity) =>
        dayjs(activity.schedules[0].timetable.day)
          .local()
          .isSameOrAfter(nextStart, "day")
      )
      .reduce((minDistance, activity) => {
        const activityDay = dayjs(activity.schedules[0].timetable.day).local();
        const distance = activityDay.diff(currentStart, "day");
        return Math.min(minDistance, distance);
      }, Infinity);

  const navigateTo = async (
    api: FullCalendar["getApi"],
    targetDate: Dayjs,
    direction: "prev" | "next"
  ) => {
    if (direction === "prev") {
      let currentViewStart = dayjs(api().view.activeStart);
      while (targetDate.isBefore(currentViewStart)) {
        api().prev();
        currentViewStart = dayjs(api().view.activeStart);
        await delay(500);
      }
    } else {
      let currentViewEnd = dayjs(api().view.activeEnd);
      while (targetDate.isSameOrAfter(currentViewEnd, "day")) {
        api().next();
        currentViewEnd = dayjs(api().view.activeEnd);
        await delay(500);
      }
    }
  };

  const visibleContains = (days: Dayjs[], target: Dayjs, unit: OpUnitType) =>
    days.some((day) => target.isSame(day, unit));

  const calendarRef = useRef<FullCalendar>(null);
  const [visibleDays, setVisibleDays] = useState<Dayjs[]>([]);
  const [currentDay, setCurrentDay] = useState<Dayjs>(dayjs());

  const setDay = (day: Dayjs) => {
    setCurrentDay(day);
    calendarRef.current?.getApi().gotoDate(day.set("hour", 12).toISOString());
  };

  const allDaysWithActivities = useMemo(() => {
    return uniq(
      activities.flatMap((a) => a.schedules).map((s) => s.timetable.day)
    )
      .map((d) => dayjs(d))
      .sort((a, b) => a.diff(b));
  }, [activities]);

  const [placeFilter, setPlaceFilter] = useState<string | undefined>(undefined);

  const handlePrev = async () => {
    // Handle verticalWeekly BEFORE calendarApi check (no FullCalendar rendered for this mode)
    if (customCalendarModel === "verticalWeekly") {
      setCurrentDay((prev) => prev.subtract(1, "month"));
      return;
    }

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (!hideEmptyDays || customCalendarModel === "horizontalMonthly") {
      calendarApi.prev();
      return;
    }

    const currentViewStartDate = dayjs(calendarApi.view.activeStart);
    const distance = computeClosestPrevDistance(
      activities,
      currentViewStartDate
    );
    if (distance === Infinity) return;
    const previousEventDate = currentViewStartDate.subtract(
      distance + 1,
      "day"
    );
    await navigateTo(() => calendarApi, previousEventDate, "prev");
  };

  const handleNext = async () => {
    // Handle verticalWeekly BEFORE calendarApi check (no FullCalendar rendered for this mode)
    if (customCalendarModel === "verticalWeekly") {
      setCurrentDay((prev) => prev.add(1, "month"));
      return;
    }

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (!hideEmptyDays || customCalendarModel === "horizontalMonthly") {
      calendarApi.next();
      return;
    }

    const currentViewStartDate = dayjs(calendarApi.view.activeStart);
    const nextViewStartDate = dayjs(calendarApi.view.activeEnd);
    const distance = computeClosestNextDistance(
      activities,
      currentViewStartDate,
      nextViewStartDate
    );
    if (distance === Infinity) return;
    const nextEventDate = currentViewStartDate.add(distance, "day");
    await navigateTo(() => calendarApi, nextEventDate, "next");
  };

  const slots = useMemo(
    () => activities.flatMap((a) => getSlots(a, isPublicExponentDisplay)),
    [activities, isPublicExponentDisplay]
  );

  const filteredSlots = placeFilter
    ? slots.filter((slot) => slot.areaName === placeFilter)
    : slots;

  const mergedSlotsForHorizontal = useMemo(() => {
    if (customCalendarModel !== "horizontalMonthly") return filteredSlots;
    return mergeConsecutiveDaySlots(filteredSlots);
  }, [filteredSlots, customCalendarModel]);

  useEffect(() => {
    if (activities.length > 0 && visibleDays.length === 0) {
      const firstActivityStart = getFirstActivityStart(activities);
      const baseDate = firstActivityStart
        ? (dayjs.max(firstActivityStart, dayjs()) as Dayjs)
        : dayjs();
      const { startOfWeek } = getStartAndEndOfWeek(baseDate.toDate());
      calendarRef.current?.getApi().gotoDate(startOfWeek.toISOString());
    }
  }, [activities, visibleDays]);

  useEffect(() => {
    if (!customCalendarModel) return;
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    const { startOfWeek } = getStartAndEndOfWeek(calendarApi.getDate());
    if (customCalendarModel === "verticalDayHour") {
      calendarApi.changeView("timeGridSevenDay");
      calendarApi.gotoDate(startOfWeek.toISOString());
    } else if (customCalendarModel === "verticalDayList") {
      calendarApi.changeView("listSevenDay");
      calendarApi.gotoDate(startOfWeek.toISOString());
    } else if (customCalendarModel === "verticalWeekly") {
      calendarApi.changeView("dayGridMonth");
    }
  }, [customCalendarModel]);

  // Sync visibleDays for verticalWeekly mode since datesSet callback is never triggered
  // (FullCalendar is not rendered in the DOM for this mode)
  useEffect(() => {
    if (customCalendarModel !== "verticalWeekly") return;

    const monthStart = currentDay.startOf("month");
    const daysInMonth = currentDay.daysInMonth();
    const days = Array.from({ length: daysInMonth }, (_, i) =>
      monthStart.add(i, "day")
    );
    setVisibleDays(days);
  }, [currentDay, customCalendarModel]);

  const groupedEvents = useMemo(
    () => groupEventsByTime(filteredSlots),
    [filteredSlots]
  );

  const groupedByWeek = useMemo(
    () => groupActivitiesByWeek(filteredSlots, currentDay.toDate()),
    [filteredSlots, currentDay]
  );

  const { minTime, maxTime } = getMinMaxEventTimes(activities, eventSchedules);

  const firstActivityDay = allDaysWithActivities[0];
  const lastActivityDay =
    allDaysWithActivities[allDaysWithActivities.length - 1];

  const isFirstDay = firstActivityDay
    ? visibleContains(visibleDays, firstActivityDay, "date")
    : false;
  const isLastDay = lastActivityDay
    ? visibleContains(visibleDays, lastActivityDay, "date")
    : false;

  const isFirstMonth = firstActivityDay
    ? visibleContains(visibleDays, firstActivityDay, "month")
    : false;
  const isLastMonth = lastActivityDay
    ? visibleContains(visibleDays, lastActivityDay, "month")
    : false;

  const groupedPlaces = groupBy(slots, (slot) => slot.areaName);

  return {
    groupedEvents,
    groupedByWeek,
    minTime,
    maxTime,
    isFirstDay,
    isLastDay,
    groupedPlaces,
    filteredSlots,
    mergedSlotsForHorizontal,
    handlePrev,
    handleNext,
    calendarRef,
    visibleDays,
    allDaysWithActivities,
    placeFilter,
    setPlaceFilter,
    isFirstMonth,
    isLastMonth,
    currentDay,
    setDay,
    setVisibleDays,
  };
};
